import { Injectable } from '@angular/core';
import { from, Observable, of, shareReplay, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AudioService {

  private audioCtx = new AudioContext();

  private bufferCache = new Map<string, AudioBuffer>();

  private requestCache = new Map<string, Observable<AudioBuffer>>();

  private MAX_CACHE_SIZE = 30;

  constructor() { }

  getAudioBuffer(src: string): Observable<AudioBuffer> {

    if (this.bufferCache.has(src)) {
      return of(this.bufferCache.get(src)!);
    }

    if (this.requestCache.has(src)) {
      return this.requestCache.get(src)!;
    }

    const request$ = from(fetch(src)).pipe(
      switchMap(res => res.arrayBuffer()),
      switchMap(arrayBuffer =>
        this.getAudioContext().decodeAudioData(arrayBuffer)
      ),
      switchMap(buffer => {
        this.addToCache(src, buffer);
        return of(buffer);
      }),
      shareReplay(1)
    );

    this.requestCache.set(src, request$);

    return request$;
  }

  getAudioContext(): AudioContext {
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      this.audioCtx = new AudioContext();
    }

    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    return this.audioCtx;
  }

  private addToCache(src: string, buffer: AudioBuffer) {
    if (this.bufferCache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.bufferCache.keys().next().value;
      if (firstKey !== undefined) {
        this.bufferCache.delete(firstKey);
        this.requestCache.delete(firstKey);
      }
    }

    this.bufferCache.set(src, buffer);
  }

  clearCache() {
    this.bufferCache.clear();
    this.requestCache.clear();
  }

  removeFromCache(src: string) {
    this.bufferCache.delete(src);
    this.requestCache.delete(src);
  }
}
